import { BreadcrumbsData, PrimaryButton } from '@systemeio/ui'
import { SaveIcon } from '@systemeio/ui/icons'
import SettingsMenuSkeleton from 'modules/profile/components/settings-menu-skeleton'
import { useSettingsMenuItems } from 'modules/profile/hooks/use-settings-menu-items'
import React, { useMemo } from 'react'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { twMerge } from 'tailwind-merge'

interface SettingsLayoutProps {
  isFetching?: boolean
  onSave?: () => void
  disabled?: boolean
  needSave?: boolean
  customHeader?: JSX.Element
  titles: BreadcrumbsData[]
  hideSettingsMenu?: boolean
}

const SettingsLayout = ({
  children,
  isFetching,
  onSave,
  disabled,
  needSave = true,
  customHeader,
  titles,
  hideSettingsMenu,
}: React.PropsWithChildren<SettingsLayoutProps>) => {
  const { t } = useLocoTranslation()
  const { user, isLoading } = useUser()

  const breadCrumbsData: BreadcrumbsData[] = useMemo(() => {
    return ([{ isStatic: true, caption: t('global.settings') }] as BreadcrumbsData[]).concat(titles)
  }, [titles, t])

  const settingsMenuItems = useSettingsMenuItems()

  return (
    <>
      <div className="mb-5 flex justify-between">
        <BreadcrumbsDashboard data={breadCrumbsData} />
        <div className={'flex gap-3'}>
          {customHeader}
          {needSave && (
            <PrimaryButton
              isFetching={isFetching}
              disabled={!user || disabled}
              onClick={onSave}
              className="flex items-center justify-between"
            >
              <SaveIcon />
              <span className="hidden sm:flex"> {t('global.save')}</span>
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className="mb-9 flex flex-col gap-3 lg:flex-row lg:gap-10">
        {!hideSettingsMenu && (
          <div className="relative w-full lg:min-h-[506px] lg:w-[250px] ">
            {isLoading ? (
              <SettingsMenuSkeleton />
            ) : (
              <ul
                className={`bg-background-secondary inset-0 flex h-fit max-h-[320px] w-full flex-col overflow-auto rounded-md text-base last:border-b-transparent lg:h-max lg:max-h-full lg:w-[250px]`}
              >
                {settingsMenuItems
                  .filter(setting => setting.shouldRender)
                  .map(setting => (
                    <li key={setting.name} className={'text-[14px]'}>
                      <LinkWithoutPrefetch
                        className={twMerge(
                          `main-transition-colors border-b-text-secondary/10 hover:bg-brand-primary-subtle focus-visible:border-b-brand-primary focus-visible:bg-brand-primary-subtle relative flex border-b px-5 py-2.5 focus:outline-hidden`,
                          setting.isActive(setting.href) &&
                            'bg-brand-primary-subtle text-brand-primary before:bg-brand-primary font-bold before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:rounded-r-[3px]',
                        )}
                        href={setting.href}
                        data-test-id={setting.dataTestId}
                      >
                        {setting.name}
                      </LinkWithoutPrefetch>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
        {children}
      </div>
    </>
  )
}

export default SettingsLayout
